import React, { useCallback, useEffect } from 'react';

import { defaultSAL1020_R01Filter, SAL1020_R01Filter, useSAL1020_R01Store } from 'src/containers/mes/SAL/SAL1020_R01/Store_SAL1020_R01';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { Tab } from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';

import { getSAL1020_R01AllCount, getSAL1020_R01DetailList, getSAL1020_R01List, getSAL1020_R01One, downloadExcel_SAL1020_R01 } from "./Api_SAL1020_R01";
import { defaultSAL1020_R01DetailDto, defaultSAL1020_R01HeaderDto, SAL1020_R01DetailDto, SAL1020_R01HeaderDto } from './Dto_SAL1020_R01';
import { AgGridReact } from 'ag-grid-react';
import { gridRefSelectedRowHandler } from 'src/utils/grid/GridHandlerContent';
import { downloadExcel } from "src/utils/blob/blobUtil";

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL1020_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<SAL1020_R01DetailDto> | null>;

	tabList: Tab[] | undefined;
	setMessage: (message: string) => void;

	containerRef: React.RefObject<HTMLDivElement | null>;
	isResizing: React.MutableRefObject<boolean>;
}

export const Content_SAL1020_R01 = (props: Props) => {

	const {
		gridRef, detailGridRef,
		tabList, setMessage,
		containerRef,
		isResizing,
	} = props;

	const {
		SAL1020_R01Filter, setSAL1020_R01Filter,
		SAL1020_R01PastFilter, setSAL1020_R01PastFilter,
		SAL1020_R01List, setSAL1020_R01List,
		editSAL1020_R01, setEditSAL1020_R01,
		selectedSAL1020_R01, setSelectedSAL1020_R01, 
		SAL1020_R01DetailList, setSAL1020_R01DetailList,
		allCount, setAllCount,
		initData,
		setScrollTop, setDetailScrollTop,
	} = useSAL1020_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	useFetchActionbarAuth("SAL1020_R01");

	const getParams = (filter: SAL1020_R01Filter) => {
		const params: Partial<SAL1020_R01Filter> = {
			compCd: filter.compCd || undefined,
			dateRange: filter.dateRange || undefined,
			customCd: filter.customCd || undefined,
			empNo: filter.empNo || undefined,
		};
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getSAL1020_R01AllCount(getParams(SAL1020_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [SAL1020_R01Filter, setAllCount]);

	const fetchData = useCallback(async () => {
		setScrollTop(null);
		setDetailScrollTop(null);
		setLoadingSpinner(true);		

		const responseData = (await getSAL1020_R01List(
			'search',
			getParams(SAL1020_R01Filter),
			SAL1020_R01List.length ?? 0
		));

		let newList = [...responseData.data.data ] as SAL1020_R01HeaderDto[];
		setSAL1020_R01List(newList);

		const processAllCount = await renewalAllCount();
		if (newList && newList.length > 0) {
			gridRefSelectedRowHandler(
				gridRef,
				setSelectedSAL1020_R01,
				() => true,
			);
			setSAL1020_R01PastFilter(SAL1020_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
			setSelectedSAL1020_R01(undefined);
			setSAL1020_R01List([]);
			setSAL1020_R01DetailList([]); 
		}
		setLoadingSpinner(false);
		return responseData;
	}, [SAL1020_R01Filter, SAL1020_R01List, setSAL1020_R01List, setSAL1020_R01PastFilter, setSelectedSAL1020_R01, selectedSAL1020_R01, setScrollTop, setDetailScrollTop])

	const fetchOne = useCallback(async () => {
		if (!selectedSAL1020_R01) return;
		setLoadingSpinner(true);
		try {
			const response = await getSAL1020_R01One(selectedSAL1020_R01.salesOrderNo!);
			setEditSAL1020_R01(response.data.data as SAL1020_R01HeaderDto);
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		}
		setLoadingSpinner(false);
	}, [selectedSAL1020_R01])

	useEffect(() => {
		const fetchDetailList = async (salesOrderNo: string) => {
			setDetailScrollTop(null);
			const responseData = (await getSAL1020_R01DetailList(salesOrderNo)).data.data;
			if(responseData && responseData.length > 0){
				setSAL1020_R01DetailList(responseData.map((value, idx) => ({
					...value,
					valueIndex: value.valueIndex ?? idx + 1,
					orderIndex: value.orderIndex ?? idx + 1,
					orderIndexSeq: value.orderIndexSeq ?? 0,
				})));
				setMessage(`${selectedSAL1020_R01?.customNm}의 상세내역 ${responseData.length}건을 조회하였습니다.`);
				setTimeout(() => setMessage(""), 5000);
			}else{
				showError('상세내역이 없습니다.');
				setSAL1020_R01DetailList([]);
			}
		}
	
		// selectedSAL1020_R01이 있으면 상세 조회 실행
		if (selectedSAL1020_R01) {
			fetchOne();
			fetchDetailList(selectedSAL1020_R01.salesOrderNo!)
		}else{
			setSAL1020_R01DetailList([]);
		}
	}, [selectedSAL1020_R01, setDetailScrollTop, setSAL1020_R01DetailList, setMessage, fetchOne])

	const handleExcelClick = useCallback(async () => {
		if (!selectedSAL1020_R01?.salesOrderNo) {
			showWarning("엑셀 다운로드할 수주번호를 선택하세요.");
			return;
		}
		
		try {
			setLoadingSpinner(true);
			const response = await downloadExcel_SAL1020_R01(selectedSAL1020_R01.salesOrderNo);
			
			// Base64 문자열 → Blob 변환 (SAL2080_R01, SAL1030_R01 패턴)
			const binaryString = atob(response.data.data);
			const byteArray = new Uint8Array(binaryString.length);
			for (let i = 0; i < binaryString.length; i++) {
				byteArray[i] = binaryString.charCodeAt(i);
			}
			const blob = new Blob([byteArray], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			});
			
			const fileName = `수주현황_${selectedSAL1020_R01.salesOrderNo}.xlsx`;
			downloadExcel(blob, fileName);
			
			showSuccess('엑셀 다운로드가 완료되었습니다.');
		} catch (error) {
			console.error('엑셀 다운로드 실패:', error);
			showError('엑셀 다운로드에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [selectedSAL1020_R01, setLoadingSpinner]);

	return {
		fetchData,
		handleExcelClick,
	};
}

